import type { MessageType } from "@expert/shared-types";

import { Container } from "@mantine/core";

import type { MessageTypeConfigRuntime } from "../../config/types";
import type { ExpertAssistMessage, ExpertAssistMetadata } from "../../shared-types";

import { MessageBubbleHeader, MessageBubbleWrapper } from "../message-bubble";
import { MessageBubbleSimplified } from "../message-bubble/MessageBubbleSimplified";
import { QuickActionGroup, type QuickActionGroupProps } from "../quick-actions";
import { MessageTag, TextMask } from "./elements";
import { MessageActions } from "./MessageActions";
import timelineClasses from "./TimelineItem.module.css";

interface MessageContentSimplifiedProps {
    type: MessageType;
    config: MessageTypeConfigRuntime;
    metadata?: ExpertAssistMetadata;
    isLastProactiveMessage: boolean;
    overflowRef: React.RefObject<HTMLDivElement | null>;
    messageIntersectionRef: (node: HTMLDivElement | null) => void;
    tag?: string;
    message?: ExpertAssistMessage;
    quickActionGroupProps: QuickActionGroupProps;
    isQuickActionsVisible: boolean;
    children: React.ReactNode;
}

export function MessageContentSimplified({
    type,
    config,
    metadata,
    isLastProactiveMessage,
    overflowRef,
    messageIntersectionRef,
    tag,
    message,
    quickActionGroupProps,
    isQuickActionsVisible,
    children,
}: MessageContentSimplifiedProps) {
    const { aiGeneratedContainer, messageBubbleAndHeaderWrapper } = timelineClasses;
    const isAiGenerated = config.bubbleStyle === "aiGenerated";
    const containerClassName = isAiGenerated
        ? `${messageBubbleAndHeaderWrapper} ${aiGeneratedContainer}`
        : messageBubbleAndHeaderWrapper;
    const shouldRenderActionsOutside = type === "SmartOffer";

    return (
        <Container className={containerClassName}>
            <MessageBubbleHeader isLastProactiveMessage={isLastProactiveMessage} type={type} metadata={metadata} />
            <MessageBubbleWrapper
                isBotListening={config.isBotListening}
                isExpanded={config.shouldShowExpanded}
                type={type}
            >
                <MessageBubbleSimplified
                    bubbleStyle={config.bubbleStyle}
                    dataVariant={config.dataVariant}
                    overflowRef={overflowRef}
                    messageIntersectionRef={messageIntersectionRef}
                    type={type}
                >
                    <TextMask isExpanded={config.shouldShowExpanded}>
                        {config.shouldShowMessageTag && tag && <MessageTag tagName={tag} />}
                        {children}
                    </TextMask>
                    <MessageActions
                        shouldRenderActionsOutside={shouldRenderActionsOutside}
                        isQuickActionsVisible={isQuickActionsVisible}
                        quickActionGroupProps={quickActionGroupProps}
                        shouldShowFeedback={config.shouldShowFeedback}
                        message={message}
                    />
                </MessageBubbleSimplified>
                {isQuickActionsVisible && shouldRenderActionsOutside && (
                    <QuickActionGroup {...quickActionGroupProps} quickActionStyle="smartOffer" />
                )}
            </MessageBubbleWrapper>
        </Container>
    );
}
